import { Image } from '../data/apartmentData';

/**
 * Image Service for handling apartment images
 * This service provides placeholder images and can be easily extended
 * to integrate with real image APIs in the future.
 */

// Configuration
const PLACEHOLDER_IMAGE_URL = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Cdefs%3E%3Cpattern id='grid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 20 0 L 0 0 0 20' fill='none' stroke='%23e0e8f0' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='400' height='300' fill='%23f8fafc' stroke='%23cbd5e1' stroke-width='2'/%3E%3Crect width='400' height='300' fill='url(%23grid)'/%3E%3Cg stroke='%23374151' stroke-width='2' fill='none'%3E%3Crect x='50' y='50' width='300' height='200'/%3E%3Cline x1='50' y1='150' x2='200' y2='150'/%3E%3Cline x1='200' y1='50' x2='200' y2='250'/%3E%3Cline x1='200' y1='200' x2='350' y2='200'/%3E%3Cline x1='280' y1='200' x2='280' y2='250'/%3E%3Crect x='60' y='60' width='20' height='8' fill='%23374151'/%3E%3Crect x='310' y='210' width='30' height='15' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Ccircle cx='320' cy='220' r='3' fill='%23374151'/%3E%3Crect x='210' y='60' width='40' height='20' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Cpath d='M220 80 Q230 85 240 80' stroke='%23374151' stroke-width='1' fill='none'/%3E%3C/g%3E%3Ctext x='125' y='110' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EWohnzimmer%3C/text%3E%3Ctext x='125' y='190' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EK%C3%BCche%3C/text%3E%3Ctext x='275' y='130' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3ESchlafzimmer%3C/text%3E%3Ctext x='315' y='235' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EBad%3C/text%3E%3Ctext x='200' y='30' text-anchor='middle' font-family='Arial, sans-serif' font-size='12' font-weight='bold' fill='%23374151'%3EGrundriss%3C/text%3E%3C/svg%3E";
const DEFAULT_IMAGE_COUNT = 5;

// Image categories for better placeholder variety
const IMAGE_CATEGORIES = [
  'Living Room',
  'Kitchen', 
  'Bedroom',
  'Bathroom',
  'Balcony',
  'Hallway',
  'Dining Room',
  'Study Room'
] as const;

/**
 * Generate placeholder images for an apartment
 * @param apartmentId - The apartment identifier
 * @param count - Number of images to generate (default: 5)
 * @returns Array of placeholder images
 */
export function generatePlaceholderImages(
  apartmentId: string, 
  count: number = DEFAULT_IMAGE_COUNT
): Image[] {
  return Array.from({ length: count }, (_, index) => ({
    url: PLACEHOLDER_IMAGE_URL,
    alt: `${apartmentId} - ${IMAGE_CATEGORIES[index % IMAGE_CATEGORIES.length]}`
  }));
}

/**
 * Get images for an apartment
 * Currently returns placeholder images, but can be extended to:
 * - Fetch from apartment data
 * - Call external image APIs
 * - Cache images locally
 * 
 * @param apartmentId - The apartment identifier
 * @param existingImages - Any existing images from apartment data
 * @returns Promise resolving to array of images
 */
export async function getApartmentImages(
  apartmentId: string,
  existingImages: Image[] = []
): Promise<Image[]> {
  // If we have existing images, use them
  if (existingImages.length > 0) {
    return existingImages;
  }

  // Try to fetch images from OnOffice API
  console.log(`[imageService] Fetching images for apartment: ${apartmentId}`);
  try {
    const images = await fetchOnOfficeImages(apartmentId, existingImages);
    console.log(`[imageService] Fetched ${images.length} images for ${apartmentId}:`, images);
    
    // If no images found, return single placeholder
    if (images.length === 0) {
      console.log(`[imageService] No images found for ${apartmentId}, using single placeholder`);
      return generatePlaceholderImages(apartmentId, 1);
    }
    
    return images;
  } catch (error) {
    console.warn('Failed to fetch images from OnOffice API, using placeholders:', error);
    return generatePlaceholderImages(apartmentId, 1);
  }
}

/**
 * Preload images for better user experience
 * @param images - Array of images to preload
 */
export function preloadImages(images: Image[]): Promise<void[]> {
  const preloadPromises = images.map(image => {
    return new Promise<void>((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load image: ${image.url}`));
      img.src = image.url;
    });
  });

  return Promise.all(preloadPromises);
}

/**
 * Get optimized image URL for different sizes
 * This can be extended to work with image CDNs that support dynamic resizing
 * 
 * @param originalUrl - The original image URL
 * @param size - The desired size ('thumbnail', 'medium', 'large')
 * @returns Optimized image URL
 */
export function getOptimizedImageUrl(
  originalUrl: string, 
  size: 'thumbnail' | 'medium' | 'large' = 'medium'
): string {
  // For the Pexels placeholder, we can add size parameters
  if (originalUrl.includes('pexels.com')) {
    const sizeParams = {
      thumbnail: '?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
      medium: '?auto=compress&cs=tinysrgb&w=800&h=600',
      large: '?auto=compress&cs=tinysrgb&w=1200&h=900'
    };
    
    return originalUrl + sizeParams[size];
  }

  // TODO: Add support for other image CDNs
  // For other URLs, return as-is for now
  return originalUrl;
}

/**
 * Image cache for better performance
 */
class ImageCache {
  private cache = new Map<string, Image[]>();
  private readonly maxSize = 50; // Maximum number of apartment image sets to cache

  set(apartmentId: string, images: Image[]): void {
    if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(apartmentId, images);
  }

  get(apartmentId: string): Image[] | undefined {
    return this.cache.get(apartmentId);
  }

  clear(): void {
    this.cache.clear();
  }
}

// Export singleton cache instance
export const imageCache = new ImageCache();

/**
 * Fetch images from OnOffice API for a specific apartment
 * @param apartmentId - The apartment identifier (InterneBemerkung like "W2.3")
 * @param existingImages - Any existing images from apartment data
 * @returns Promise resolving to array of images from OnOffice
 */
async function fetchOnOfficeImages(apartmentId: string, _existingImages: Image[]): Promise<Image[]> {
  // Check cache first
  const cachedImages = imageCache.get(apartmentId);
  if (cachedImages) {
    return cachedImages;
  }

  // Need to get the OnOffice estate ID from apartment data
  // For now, we'll look for it in the apartment data context
  const estateId = await getEstateIdFromApartmentId(apartmentId);
  console.log(`[fetchOnOfficeImages] Estate ID for ${apartmentId}:`, estateId);
  
  if (!estateId) {
    console.warn(`No OnOffice estate ID found for apartment ${apartmentId}`);
    return [];
  }

  const apiUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4000/api/onoffice';
  console.log(`[fetchOnOfficeImages] VITE_BACKEND_URL:`, import.meta.env.VITE_BACKEND_URL);
  console.log(`[fetchOnOfficeImages] Using API URL:`, apiUrl);
  const imageApiUrl = `${apiUrl}/images/${estateId}`;
  console.log(`[fetchOnOfficeImages] Calling API:`, imageApiUrl);
  const response = await fetch(imageApiUrl);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch images: ${response.status} ${response.statusText}`);
  }

  const { images } = await response.json();
  
  const transformedImages: Image[] = images.map((img: any) => ({
    url: img.url,
    alt: img.title || `${apartmentId} - ${img.filename || 'Image'}`
  }));

  // Cache the results
  imageCache.set(apartmentId, transformedImages);
  
  return transformedImages;
}

/**
 * Helper function to get OnOffice estate ID from apartment ID
 * This function looks up the estate ID by making a fresh API call if needed
 */
async function getEstateIdFromApartmentId(apartmentId: string): Promise<string | null> {
  // First try to get from cached apartment data on window
  if ((window as any).__PLAZA_APARTMENT_DATA__) {
    const apartments = (window as any).__PLAZA_APARTMENT_DATA__;
    const apartment = apartments.find((apt: any) => apt.id === apartmentId);
    return apartment?.internalId || null;
  }
  
  // If no cached data, fetch apartments and find the matching one
  try {
    const { fetchApartmentsAPI } = await import('../data/apartmentData');
    const apartments = await fetchApartmentsAPI();
    
    // Cache apartments data on window for future use
    (window as any).__PLAZA_APARTMENT_DATA__ = apartments;
    
    const apartment = apartments.find(apt => apt.id === apartmentId);
    return apartment?.internalId || null;
  } catch (error) {
    console.error('Failed to fetch apartment data for estate ID lookup:', error);
    return null;
  }
} 